import { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { NotificationBox } from '@eneco/sparky';
import { TextProps } from '@eneco/sparky/types';

import { Fields } from '../../types';
import type { FlowNotificationContent } from '../../types/content';
import { readField } from '../../utils/readField';
import { Header } from '../Header/Header';

export { STEP_TITLE_ID, getRadioGroupNameProps } from './radioGroupName';

/** The same three fields as `Fields`, without Sitecore's `{ value: … }` wrappers. */
export interface StepHeaderContent {
  content?: {
    title?: string;
    text?: string;
    notification?: FlowNotificationContent;
  };
}

interface Props {
  textualData?: Fields | StepHeaderContent;
  /**
   * A step that reads flat content passes its fields here instead of handing over `textualData`,
   * and each one wins over the field of the same name in it. That lets steps move to the flat
   * names one at a time while unmigrated ones keep passing `textualData`, which goes once the
   * last of them has moved. `notification.content` is HTML, the form both adapters deliver it in.
   */
  title?: string;
  text?: string;
  notification?: FlowNotificationContent;
  align?: TextProps['align'];
}

export const StepHeader: FC<Props> = ({
  textualData,
  title: titleProp,
  text: textProp,
  notification: notificationProp,
  align = 'left',
}) => {
  const content = textualData?.content;
  const title = titleProp ?? readField<string>(content?.title);
  const text = textProp ?? readField<string>(content?.text);
  const notification = notificationProp ?? readField<FlowNotificationContent>(content?.notification);

  return (
    <>
      <Header align={align}>
        <Header.Title>{title}</Header.Title>
        {text && <Header.Text>{text}</Header.Text>}
      </Header>

      {notification?.content && (
        <NotificationBox
          isAlert={false}
          headingLevel="h2"
          variant={notification.variant}
          title={notification.title}
          text={<RichText html={notification.content} />}
        />
      )}
    </>
  );
};
